'use client'

import { useEffect, useMemo, useRef } from 'react'

import { experimentsTrackEvent } from '../eventTracking'
import {
  EXPERIMENTS_TRACK_EVENT_LOGIN_OTP_FAILURE,
  EXPERIMENTS_TRACK_EVENT_LOGIN_OTP_PAGE_VIEW,
  EXPERIMENTS_TRACK_EVENT_LOGIN_OTP_SUBMIT,
  EXPERIMENTS_TRACK_EVENT_LOGIN_SWITCH_TO_PASSWORD,
} from '../eventTracking/EXPERIMENTS_TRACK_EVENT'
import { useGetExperiment } from '../hooks'
import { EXPERIMENT_NAME_LOGIN_TWO_STEP } from '../model/EXPERIMENT_NAME'
import { EXPERIMENT_VARIATION } from '../model/EXPERIMENT_VARIATION'
import { type ExperimentType } from '../model/Experiment.types'

import {
  type LoginOtpTracking,
  type LoginOtpTrackingAction,
} from './loginOtpTracking'

const eventByAction: Record<LoginOtpTrackingAction, string> = {
  failure: EXPERIMENTS_TRACK_EVENT_LOGIN_OTP_FAILURE,
  page_view: EXPERIMENTS_TRACK_EVENT_LOGIN_OTP_PAGE_VIEW,
  submit: EXPERIMENTS_TRACK_EVENT_LOGIN_OTP_SUBMIT,
  switch_to_password: EXPERIMENTS_TRACK_EVENT_LOGIN_SWITCH_TO_PASSWORD,
}

const resolveTwoStepLoginOtpTracking = (
  experiment: Pick<ExperimentType, 'isEnabled' | 'variant'>,
): LoginOtpTracking => ({
  track: (action): void => {
    if (
      !experiment.isEnabled ||
      experiment.variant !== EXPERIMENT_VARIATION.V1
    ) {
      return
    }

    experimentsTrackEvent(eventByAction[action])
  },
})

export const useTwoStepLoginOtpTracking = (): LoginOtpTracking => {
  const hasTrackedPageView = useRef(false)
  const { isEnabled, variant } = useGetExperiment(
    EXPERIMENT_NAME_LOGIN_TWO_STEP,
  )
  const tracking = useMemo(
    () => resolveTwoStepLoginOtpTracking({ isEnabled, variant }),
    [isEnabled, variant],
  )

  useEffect(() => {
    if (hasTrackedPageView.current) {
      return
    }

    hasTrackedPageView.current = true
    tracking.track('page_view')
  }, [tracking])

  return tracking
}
